/**
 * ConsentBanner Component
 * Manages user cookie consent and terms acceptance banners.
 * Persists cookie preferences and terms acknowledgment in LocalStorage.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// Storage keys for cookie and terms preferences
const COOKIE_KEY = 'clipstone_cookie_consent';
const COOKIE_REJECTED_KEY = 'clipstone_cookie_rejected';
const TERMS_KEY = 'clipstone_terms_accepted';

const ConsentBanner = () => {
  // Synchronously initialize state from LocalStorage to prevent modal and banner flash on initial render
  const [cookiesAccepted, setCookiesAccepted] = useState(() => {
    try {
      return window.localStorage.getItem(COOKIE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [cookiesRejected, setCookiesRejected] = useState(() => {
    try {
      return window.localStorage.getItem(COOKIE_REJECTED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [termsAccepted, setTermsAccepted] = useState(() => {
    try {
      return window.localStorage.getItem(TERMS_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [showSummary, setShowSummary] = useState(false);

  // Sync state if localStorage changes in another tab
  useEffect(() => {
    const syncStorage = () => {
      try {
        setCookiesAccepted(window.localStorage.getItem(COOKIE_KEY) === 'true');
        setCookiesRejected(window.localStorage.getItem(COOKIE_REJECTED_KEY) === 'true');
        setTermsAccepted(window.localStorage.getItem(TERMS_KEY) === 'true');
      } catch {
        // ignore storage errors
      }
    };

    window.addEventListener('storage', syncStorage);
    return () => window.removeEventListener('storage', syncStorage);
  }, []);

  /** Accepts non-essential cookies and stores preference */
  const handleAcceptCookies = () => {
    window.localStorage.setItem(COOKIE_KEY, 'true');
    window.localStorage.removeItem(COOKIE_REJECTED_KEY);
    setCookiesAccepted(true);
    setCookiesRejected(false);
  };

  const handleRejectCookies = () => {
    window.localStorage.setItem(COOKIE_REJECTED_KEY, 'true');
    setCookiesRejected(true);
  };

  const handleAcceptTerms = () => {
    window.localStorage.setItem(TERMS_KEY, 'true');
    setTermsAccepted(true);
  };

  const [summaryChecked, setSummaryChecked] = useState(false);

  const handleOpenSummary = () => {
    setSummaryChecked(false);
    setShowSummary(true);
  };
  const handleCloseSummary = () => setShowSummary(false);
  const handleConfirmSummary = () => {
    if (!summaryChecked) return;
    handleAcceptTerms();
    handleCloseSummary();
  };

  return (
    <>
      {!termsAccepted && (
        <div className="terms-overlay">
          <div className="terms-modal">
            <h2 className="text-lg font-bold">Terms of Service Agreement</h2>
            <p>Before using this website, please agree to our Terms of Service.</p>
            <div className="terms-actions">
              <button className="btn btn-outline" onClick={handleOpenSummary}>
                Read Terms
              </button>
              <button className="btn btn-primary" onClick={handleAcceptTerms}>
                I Agree
              </button>
            </div>
            <p className="terms-note">You will not be able to continue using the site until you agree.</p>

            {showSummary && (
              <div className="terms-summary-modal">
                <button className="terms-summary-close" onClick={handleCloseSummary} aria-label="Close summary">
                  ×
                </button>
                <h3>Summary of Terms</h3>
                <p>These are the key points of the website terms of service:</p>
                <ul className="terms-summary-list">
                  <li>Use the site responsibly and do not abuse the service.</li>
                  <li>Your actions on the site must comply with the posted rules.</li>
                  <li>We may update the terms and continued use means you accept changes.</li>
                  <li>Access may be blocked if anyone violates the terms.</li>
                  <li>The website is provided as-is, and we are not liable for misuse.</li>
                </ul>
                <p>If you want the full detailed terms, please contact us or review them later on the Terms page.</p>
                <label className="terms-summary-checkbox">
                  <input
                    type="checkbox"
                    checked={summaryChecked}
                    onChange={(event) => setSummaryChecked(event.target.checked)}
                  />
                  I have read the summary and agree to follow these rules.
                </label>
                <div className="terms-summary-actions">
                  <button className="btn btn-secondary" onClick={handleCloseSummary}>
                    Close
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleConfirmSummary}
                    disabled={!summaryChecked}
                  >
                    Confirm Agreement
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {!cookiesAccepted && !cookiesRejected && (
        <div className="cookie-banner">
          <div className="cookie-banner__text">
            <strong>Cookie notice:</strong> We use cookies to improve your experience and remember your preferences.
            <Link to="/privacy"> Learn more</Link>.
          </div>
          <div className="cookie-banner__actions">
            <button className="btn btn-primary" onClick={handleAcceptCookies}>
              Accept Cookies
            </button>
            <button className="btn btn-secondary" onClick={handleRejectCookies}>
              Reject Cookies
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ConsentBanner;

